import { useAnalytics, withEventContext } from "@codat/analytics";
import {
  ArrowThinUpIcon,
  ColumnDefinition,
  DataTable,
  Typography,
  ZeroState,
} from "@codat/orchard-ui";
import { PushOperation } from "api/models/PushOperation";
import { PagedResponse } from "api/shared";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { formatDate, getTimeToComplete } from "utils/timeFormaters";

import IdCell from "components/IdCell";
import PaginationRange from "components/PaginationRange";
import ValidationErrorCell from "components/ValidationErrorCell";
import PushOperationErrorModal, {
  PushOperationErrorModalProps,
} from "pages/PushHistory/ErrorModal";

import { usePublicService } from "../../contexts/PublicServiceContext";
import styles from "./PushHistory.module.scss";
import { StateIndicatorLabel } from "./PushOperationStateIndicatorLabel";
import ConnectionErrorBanner from "components/ConnectionErrorBanner";

const pageName = "Write history";

const DatatypeCell: React.FC<{ value: { datatype: string } }> = ({
  value: { datatype },
}) => {
  return (
    <div className={styles.cell}>
      <Typography as="p" variant="body_sm">
        {datatype}
      </Typography>
    </div>
  );
};

const RequestedDateCell: React.FC<{ value: { requested: string } }> = ({
  value: { requested },
}) => {
  return (
    <div className={styles.cell}>
      <Typography as="p" variant="body_sm">
        {formatDate(requested)}
      </Typography>
    </div>
  );
};

const TimeToCompleteCell: React.FC<{
  value: { requested: string; completed: string };
}> = ({ value: { requested, completed } }) => {
  return (
    <div className={styles.cell}>
      <Typography as="p" variant="body_sm">
        {getTimeToComplete(
          Date.parse(requested),
          completed ? Date.parse(completed) : null
        )}
      </Typography>
    </div>
  );
};

const StateCell: React.FC<{ value: { state: string } }> = ({
  value: { state },
}) => {
  return (
    <div className={styles.cell}>{<StateIndicatorLabel state={state} />}</div>
  );
};

const pageSize = 50;

function getErrorsForItem(item: PushOperation) {
  if (item.validation?.errors?.length) {
    return item.validation.errors;
  }
  if (item.errorMessage) {
    return [item.errorMessage];
  }
}

const PushHistory: React.FC = () => {
  const { sendPageViewEvent } = useAnalytics();
  const { search } = useLocation();
  const history = useHistory();
  const [pushHistoryResult, setPushHistoryResult] =
    useState<PagedResponse<PushOperation>>();
  const [dataHistoryError, setDataHistoryError] = useState();
  const [dataHistoryIsLoading, setDataHistoryIsLoading] = useState(true);
  const [dataConnectionError, setDataConnectionError] = useState(false);
  const [errorModalData, setErrorModalData] = useState<Omit<
    PushOperationErrorModalProps,
    "onClose"
  > | null>(null);

  function getNextPollDelay(currentDelay: number): number {
    return currentDelay < 30000 ? currentDelay * 2 : currentDelay;
  }

  const pageQuery = Number(new URLSearchParams(search).get("page"));

  const [currentPage, setCurrentPage] = useState(
    pageQuery && pageQuery > 0 ? pageQuery : 1
  );
  const { companyId } = useParams<{ companyId: string }>();
  const { getPushOperations, getConnection } = usePublicService();

  if (!companyId) {
    throw new Error("No companyId provided.");
  }

  useEffect(() => {
    sendPageViewEvent(pageName);
  }, [sendPageViewEvent]);

  function transformDataSet(pushOperation?: PushOperation[]) {
    if (!pushOperation) return [];

    return pushOperation.map((row) => ({
      data: [
        {
          key: "type",
          value: {
            datatype: row.dataType,
          },
        },
        {
          key: "id",
          value: {
            id: row.pushOperationKey,
            name: "Push Operation ID",
            copySuccessMessage: "Copied push operation ID to the clipboard",
            clipboardClassName: styles.hover,
            ["data-testid"]: "push-operation-id-cell",
          },
        },
        {
          key: "requestedDate",
          value: { requested: row.requestedOnUtc },
        },
        {
          key: "timeToComplete",
          value: {
            requested: row.requestedOnUtc,
            completed: row.completedOnUtc,
          },
        },
        {
          key: "status",
          value: {
            state: row.status,
          },
        },
        {
          key: "error",
          value: {
            companyId,
            datasetId: row.pushOperationKey,
            onClick:
              row.status === "Failed" &&
              (row.errorMessage || row.validation?.errors.length)
                ? () =>
                    setErrorModalData({
                      companyId,
                      id: row.pushOperationKey,
                      dataType: row.dataType,
                      requestedAt: row.requestedOnUtc,
                      errors: getErrorsForItem(row),
                    })
                : null,
          },
        },
      ],
    }));
  }

  const poller = useCallback(
    (delay = 2000) => {
      getPushOperations(companyId, currentPage, pageSize)
        .then((response) => {
          setPushHistoryResult(response);

          Promise.all(
            [
              ...new Set(
                response.results.map((push) => push.dataConnectionKey)
              ),
            ].map((connectionId) => {
              getConnection(companyId, connectionId).then((res) => {
                setDataConnectionError(
                  (d) =>
                    d ||
                    (res.dataConnectionErrors &&
                      res.dataConnectionErrors.filter(
                        (x) => x.status === "Active"
                      ).length > 0)
                );
              });
            })
          );

          if (response.results.some((r) => r.status == "Pending")) {
            setTimeout(() => poller(getNextPollDelay(delay)), delay);
          }
        })
        .catch((e) => {
          setDataHistoryError(e);
        })
        .finally(() => {
          setDataHistoryIsLoading(false);
        });
    },
    [companyId, currentPage, getPushOperations, getConnection]
  );

  useEffect(() => {
    setDataHistoryIsLoading(true);
    poller();
  }, [poller]);

  const totalPages = Math.ceil(
    (pushHistoryResult?.totalResults ?? 1) / pageSize
  );

  const transformedData = transformDataSet(pushHistoryResult?.results);

  const columnDefinitions: ColumnDefinition<string>[] = [
    {
      key: "type",
      label: "Type",
      component: DatatypeCell,
    },
    {
      key: "id",
      label: "Push Operation ID",
      component: IdCell,
    },
    {
      key: "requestedDate",
      label: "Requested date",
      component: RequestedDateCell,
    },
    {
      key: "timeToComplete",
      label: "Time to complete",
      component: TimeToCompleteCell,
    },
    {
      key: "status",
      label: "Status",
      component: StateCell,
    },
    {
      key: "error",
      label: "",
      component: ValidationErrorCell,
    },
  ];

  return (
    <div>
      <Typography
        as="h2"
        variant="heading_xxl"
        data-testid="push-history-title"
      >
        Write history
      </Typography>
      {dataConnectionError && <ConnectionErrorBanner companyId={companyId} />}
      {dataHistoryError ? (
        <ZeroState
          data-testid="push-history-zero-state"
          title="Error loading data, please try again."
          ctaText="Reload data"
          onCtaClick={window.location.reload}
          className={styles.zeroState}
        />
      ) : (
        <>
          {errorModalData?.errors && (
            <PushOperationErrorModal
              {...errorModalData}
              onClose={() => setErrorModalData(null)}
            />
          )}
          {pushHistoryResult?.totalResults === 0 ? (
            <ZeroState
              className={styles.zeroState}
              icon={<ArrowThinUpIcon />}
              title="No write operations"
              description="Write operation history will appear here"
            />
          ) : (
            <>
              <DataTable
                className={styles.dataTable}
                data-testid="push-history-table"
                rowsInfo={transformedData}
                columnDefs={columnDefinitions}
                isSkeleton={dataHistoryIsLoading}
                skeletonRows={pageSize}
                isLayoutAuto
                hasBorder
              />
              <PaginationRange
                page={currentPage}
                total={totalPages}
                onUpdatePageIndex={(page) => {
                  history.push({
                    pathname: location.pathname,
                    search: `?page=${page}`,
                  });
                  setCurrentPage(page);
                }}
                isPrevNext
                className={styles.paginationNumbers}
                pageSize={pageSize}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default withEventContext(PushHistory, pageName);
